import { batch, createSignal, type Accessor } from 'solid-js';
import type { RgbColor } from 'solid-color';
import tinycolor from 'tinycolor2';

const COLOR_PICKER_OFFSET_PX = 4;

export interface PickerPosition {
  readonly left: number;
  readonly top: number;
}

export interface PaletteStopColorPickerControls {
  readonly showColorPicker: Accessor<boolean>;
  readonly pickerPosition: Accessor<PickerPosition>;
  setSwatchAnchor(el: HTMLDivElement): void;
  toggleColorPicker(): void;
  closeColorPicker(): void;
  saveColor(rgb: RgbColor): string;
}

/**
 * Coordinates a set of colour pickers so at most one is open at a time.
 * Opening a picker closes (cancels) whichever picker was previously open.
 */
export interface PaletteStopColorPickerGroup {
  /** Register a picker's close handler; opening it closes any other member. */
  requestOpen(closeSelf: () => void): void;
  /** Forget a picker once it has closed. */
  notifyClosed(closeSelf: () => void): void;
}

export function createPaletteStopColorPickerGroup(): PaletteStopColorPickerGroup {
  let activeClose: (() => void) | undefined;

  return {
    requestOpen(closeSelf: () => void): void {
      const previousClose = activeClose;
      activeClose = closeSelf;
      // Close the previously open picker after claiming the active slot so its
      // own `notifyClosed` does not clear the slot we just took.
      if (previousClose && previousClose !== closeSelf) {
        previousClose();
      }
    },
    notifyClosed(closeSelf: () => void): void {
      if (activeClose === closeSelf) {
        activeClose = undefined;
      }
    },
  };
}

export function toPickerHexColor(color: string): string {
  return color.startsWith('#') ? color : `#${color}`;
}

/**
 * Shared swatch-anchored colour picker state used by stop row and new-stop
 * inputs. Passing a shared `group` accessor ensures only one picker in that
 * group is open at once — opening this one cancels any other that was open. The
 * group is taken as an accessor so callers can forward a reactive prop without
 * reading it eagerly at setup.
 */
export function createPaletteStopColorPickerControls(
  group?: Accessor<PaletteStopColorPickerGroup | undefined>
): PaletteStopColorPickerControls {
  const [showColorPicker, setShowColorPicker] = createSignal(false);
  const [pickerPosition, setPickerPosition] = createSignal<PickerPosition>({
    left: 0,
    top: 0,
  });
  let swatchAnchor: HTMLDivElement | undefined;

  function setSwatchAnchor(el: HTMLDivElement): void {
    swatchAnchor = el;
  }

  function openColorPicker(): void {
    batch(() => {
      if (swatchAnchor) {
        const rect = swatchAnchor.getBoundingClientRect();
        setPickerPosition({
          left: rect.left,
          top: rect.bottom + COLOR_PICKER_OFFSET_PX,
        });
      }
      group?.()?.requestOpen(closeColorPicker);
      setShowColorPicker(true);
    });
  }

  function toggleColorPicker(): void {
    if (showColorPicker()) {
      closeColorPicker();
    } else {
      openColorPicker();
    }
  }

  function closeColorPicker(): void {
    setShowColorPicker(false);
    group?.()?.notifyClosed(closeColorPicker);
  }

  function saveColor(rgb: RgbColor): string {
    const hex = tinycolor(rgb).toHexString();
    closeColorPicker();
    return hex;
  }

  return {
    showColorPicker,
    pickerPosition,
    setSwatchAnchor,
    toggleColorPicker,
    closeColorPicker,
    saveColor,
  };
}
